import moment from "moment";

/**
 * Appointments come from two unreconciled backend stores:
 *
 *  1. LEGACY - AppointmentMaster/AppointmentDetails, read by
 *     Patient/GetUpComingAppointment and Doctor/GetAllAppointment.
 *  2. ZOOM   - the newer `Appointment` table written by bookdoctorappointment,
 *     read by Patient/GetPatientAppointmentList and Doctor/GetDrappointmentList.
 *
 * No backend read path joins them, so anything booked through the Zoom flow is
 * invisible to the legacy endpoint and vice versa. Both are normalized to one
 * shape here and merged in the Appointments screens.
 *
 */

export const SOURCE_LEGACY = "legacy";
export const SOURCE_ZOOM = "zoom";

function normalizeName(value) {
  return String(value || "").trim().toLowerCase();
}

// AvilibilityType is the actual booked modality (In Person / Virtual), from
// the EnumValue lookup in GetPatientAppointmentDetailsInRange/
// GetDoctorAppointmentDetailsInRange - same enum ZoomAvailability.jsx's type
// dropdown uses ("Virtual" / "In Person"). Case/spacing-insensitive match
// since we don't control the exact string the backend returns.
function modalityFromAvilibilityType(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return null;
  if (v.includes("virtual")) return "Virtual";
  if (v.includes("person") || v.includes("in-person")) return "In-Person";
  return null;
}

/** Legacy record -> unified shape. Dates are "DD-MM-YYYY" / "DD-MM-YYYY HH:mm". */
function fromLegacy(appt, { providerField }) {
  const start = appt?.startTime ? moment(appt.startTime, "DD-MM-YYYY HH:mm") : null;
  const end = appt?.endTime ? moment(appt.endTime, "DD-MM-YYYY HH:mm") : null;
  const fallbackDate = appt?.appointDate ? moment(appt.appointDate, "DD-MM-YYYY") : null;
  const startMoment = start && start.isValid() ? start : fallbackDate;

  return {
    source: SOURCE_LEGACY,
    id: appt?.appointId ?? null,
    providerName: appt?.[providerField] ?? appt?.name ?? "",
    roleName: appt?.roleName ?? "",
    startMoment: startMoment && startMoment.isValid() ? startMoment : null,
    endMoment: end && end.isValid() ? end : null,
    // Prefer the real modality field if the legacy record happens to carry
    // one; otherwise fall back to the meeting-link heuristic (legacy has no
    // guaranteed AvilibilityType field, unlike the Zoom store).
    locationLabel:
      modalityFromAvilibilityType(appt?.avilibilityType ?? appt?.AvilibilityType) ??
      (appt?.meetingLink ? "Virtual" : "In-Person"),
    meetingLink: appt?.meetingLink || null,
    approved: appt?.approved ?? null,
    raw: appt,
  };
}

/** Zoom record -> unified shape. Date is "YYYY-MM-DD HH:mm". */
function fromZoom(appt, { providerField }) {
  const rawDateTime =
    appt?.appoinmentDateTime ?? appt?.appointmentDateTime ?? appt?.AppoinmentDateTime;
  const startMoment = rawDateTime ? moment(rawDateTime, "YYYY-MM-DD HH:mm") : null;

  return {
    source: SOURCE_ZOOM,
    id: appt?.appintmentId ?? appt?.appointmentId ?? appt?.AppintmentId ?? null,
    providerName: appt?.[providerField] ?? "",
    roleName: "",
    startMoment: startMoment && startMoment.isValid() ? startMoment : null,
    endMoment: null,
    // AvilibilityType is the actual booked modality - prefer it over
    // guessing from the location string, which used to default to "Virtual"
    // whenever location came back blank (wrongly labeling in-person visits
    // as virtual). Location text (e.g. a city name) still wins when present,
    // for the more specific "Calgary" style label; only the true/false
    // Virtual-vs-In-Person call comes from AvilibilityType now.
    locationLabel:
      appt?.location || appt?.Location
        ? String(appt.location || appt.Location).trim().toLowerCase() === "virtual"
          ? "Virtual"
          : appt.location || appt.Location
        : modalityFromAvilibilityType(appt?.avilibilityType ?? appt?.AvilibilityType) ?? "Virtual",
    meetingLink: appt?.meetingLink || appt?.MeetingLink || null,
    approved: true,
    email: appt?.email ?? appt?.Email ?? null,
    phone: appt?.phonenumber ?? appt?.phoneNumber ?? null,
    raw: appt,
  };
}

/**
 * Merge both sources into one sorted, de-duplicated list.
 *
 * @param {object} opts
 * @param {Array} opts.legacy       records from the legacy endpoint
 * @param {Array} opts.zoom         records from the Zoom endpoint
 * @param {"patient"|"doctor"} opts.viewer whose perspective (picks the other party's name field)
 */
export function mergeAppointments({ legacy = [], zoom = [], viewer = "patient" }) {
  // From the patient's side the counterparty is the doctor, and vice versa.
  const legacyProviderField = "name";
  const zoomProviderField = viewer === "doctor" ? "patientName" : "doctortName";

  const normalizedLegacy = (Array.isArray(legacy) ? legacy : []).map((a) =>
    fromLegacy(a, { providerField: legacyProviderField })
  );

  const normalizedZoom = (Array.isArray(zoom) ? zoom : []).map((a) =>
    fromZoom(a, { providerField: zoomProviderField })
  );

  const all = [...normalizedZoom, ...normalizedLegacy].filter((a) => a.startMoment);

  // The same booking can surface in both stores; prefer the Zoom record since it
  // carries the real location string.
  const seen = new Set();
  const deduped = [];
  all.forEach((a) => {
    const key = `${normalizeName(a.providerName)}|${a.startMoment.format("YYYY-MM-DD HH:mm")}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(a);
  });

  return deduped;
}

/** Split: upcoming (soonest first) and past (most recent first). */
export function splitUpcomingPast(appointments) {
  const now = moment();
  const upcoming = [];
  const past = [];

  (appointments || []).forEach((a) => {
    if (a.startMoment.isSameOrAfter(now)) upcoming.push(a);
    else past.push(a);
  });

  upcoming.sort((a, b) => a.startMoment.valueOf() - b.startMoment.valueOf());
  past.sort((a, b) => b.startMoment.valueOf() - a.startMoment.valueOf());

  return { upcoming, past };
}
